<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useQuery, useMutation } from '@tanstack/vue-query';
import { shiftImportsApi, type ShiftImport, type ShiftImportRowError } from '@/api/shift-imports';
import { MONTH_NAMES } from '@/utils/month-names';
import ResponsiveDialog from '@/components/responsive-dialog.vue';

const POLLING_INTERVAL = 3000;
// `pending` counts as processing: the create redirect races Sidekiq, so the first
// fetch almost always sees `pending` — it must poll, not render a result screen.
const PROCESSING_STATUSES = ['pending', 'analyzing', 'committing'];

const { t } = useI18n();

interface Props {
  shiftImportId: number;
}
const props = defineProps<Props>();

const isPolling = ref(false);
// Set when the user confirms: the commit endpoint responds before the job flips
// `previewed → committing`, so we keep polling until the status actually moves.
const awaitingCommit = ref(false);

const { data: shiftImport, isPending, refetch } = useQuery<ShiftImport>({
  queryKey: ['shiftImport', props.shiftImportId],
  queryFn: async () => {
    const result = await shiftImportsApi.show(props.shiftImportId);
    if (awaitingCommit.value && result.status !== 'previewed') awaitingCommit.value = false;
    isPolling.value = PROCESSING_STATUSES.includes(result.status) || awaitingCommit.value;

    return result;
  },
  refetchInterval: computed(() => (isPolling.value ? POLLING_INTERVAL : false)),
});

const status = computed(() => shiftImport.value?.status);
// Serialized ShiftImport#warnings? — a warned preview cannot be committed (the
// backend policy rejects it too); the user fixes the data and re-uploads.
const hasWarnings = computed(() => !!shiftImport.value?.warnings);

// Parser errors carry a single `row`; group-level failures (e.g. a rejected
// CreateShiftsJob) carry `rows` — every spreadsheet row that fed the group.
function rowErrorTitle(error: ShiftImportRowError): string {
  if (error.row) return t('shiftImport.rowErrorLine', { row: error.row, message: error.message });
  if (error.rows?.length === 1) {
    return t('shiftImport.rowErrorLine', { row: error.rows[0], message: error.message });
  }
  if (error.rows?.length) {
    return t('shiftImport.rowErrorLines', { rows: error.rows.join(', '), message: error.message });
  }

  return error.message;
}

function rowErrorDnis(error: ShiftImportRowError): string | undefined {
  if (!error.patientDni && !error.caregiverDni) return undefined;

  return t('shiftImport.rowErrorDnis', {
    patient: error.patientDni ?? '—',
    caregiver: error.caregiverDni ?? '—',
  });
}

// Re-upload replaces any retry flow: whether the fix lives in Kuido (missing
// records) or in the sheet itself, the user corrects it and uploads again —
// duplicate detection makes overlapping uploads safe.
const periodLabel = computed(() => {
  if (!shiftImport.value) return '';

  return `${MONTH_NAMES[shiftImport.value.month - 1]} ${shiftImport.value.year}`;
});

// A clean preview that would create nothing (all duplicates) has no reason to
// commit — hide confirm and offer re-upload instead, so the page is never a
// dead end.
const canConfirm = computed(() => status.value === 'previewed' &&
  !hasWarnings.value &&
  (shiftImport.value?.preview.wouldCreate ?? 0) > 0);

const canReupload = computed(() => {
  if (!shiftImport.value) return false;
  if (status.value === 'failed' || status.value === 'completed_with_warnings') return true;

  return status.value === 'previewed' && !canConfirm.value;
});

const reuploadHref = computed(() => {
  if (!shiftImport.value) return '/shift_imports/new';

  return `/shift_imports/new?month=${shiftImport.value.month}&year=${shiftImport.value.year}`;
});

const { mutate: confirmImport, isPending: isCommitting, isError: commitFailed } = useMutation({
  mutationFn: () => shiftImportsApi.commit(props.shiftImportId),
  onSuccess: () => {
    awaitingCommit.value = true;
    isPolling.value = true;
    refetch();
  },
  // A stale tab can 401 here (import re-committed elsewhere, permission or
  // warnings changed) — resync so the page reflects the real state.
  onError: () => refetch(),
});

const showConfirmDialog = ref(false);

function confirmCommit() {
  showConfirmDialog.value = false;
  confirmImport();
}
</script>

<template>
  <v-container class="v-col-12 v-col-md-8">
    <div
      v-if="isPending || !shiftImport"
      class="d-flex justify-center pa-8"
    >
      <v-progress-circular
        indeterminate
        color="primary"
      />
    </div>

    <template v-else>
      <h2 class="text-h6 mb-4">
        {{ t('shiftImport.periodTitle', { period: periodLabel }) }}
      </h2>

      <div
        v-if="PROCESSING_STATUSES.includes(shiftImport.status)"
        class="d-flex flex-column align-center pa-8"
      >
        <v-progress-circular
          indeterminate
          color="primary"
          size="64"
        />
        <p class="mt-4 text-body-1">
          {{ status === 'committing' ? t('shiftImport.committing') : t('shiftImport.analyzing') }}
        </p>
      </div>

      <template v-else-if="status === 'failed'">
        <v-alert
          type="error"
          class="error-alert-preline"
        >
          {{ shiftImport.errorMessage || t('shiftImport.failed') }}
        </v-alert>
        <v-row class="ma-0 mt-4">
          <v-btn
            variant="outlined"
            prepend-icon="mdi-arrow-left"
            :href="reuploadHref"
          >
            {{ t('shiftImport.reupload') }}
          </v-btn>
          <v-spacer />
        </v-row>
      </template>

      <v-card
        v-else
        class="pa-4"
      >
        <v-card-title>
          {{ status === 'previewed'
            ? t('shiftImport.previewTitle')
            : (hasWarnings ? t('shiftImport.completedWithWarningsTitle') : t('shiftImport.completedTitle')) }}
        </v-card-title>

        <div class="d-flex flex-wrap ga-3 my-3">
          <v-chip
            v-if="status === 'previewed'"
            color="primary"
            size="large"
          >
            {{ t('shiftImport.wouldCreate') }}: {{ shiftImport.preview.wouldCreate ?? 0 }}
          </v-chip>
          <v-chip
            v-if="status === 'previewed'"
            color="grey"
            size="large"
          >
            {{ t('shiftImport.wouldDuplicate') }}: {{ shiftImport.preview.wouldDuplicate ?? 0 }}
          </v-chip>
          <v-chip
            v-if="status === 'previewed' && (shiftImport.preview.wouldFail ?? 0) > 0"
            color="error"
            size="large"
          >
            {{ t('shiftImport.wouldFail') }}: {{ shiftImport.preview.wouldFail }}
          </v-chip>
          <v-chip
            v-if="status !== 'previewed'"
            color="success"
            size="large"
          >
            {{ t('shiftImport.created') }}: {{ shiftImport.createdCount }}
          </v-chip>
          <v-chip
            v-if="status !== 'previewed'"
            color="grey"
            size="large"
          >
            {{ t('shiftImport.duplicates') }}: {{ shiftImport.duplicateCount }}
          </v-chip>
        </div>

        <template v-if="hasWarnings">
          <p
            v-if="status !== 'previewed'"
            class="text-medium-emphasis text-caption mb-2"
          >
            {{ t('shiftImport.notCreatedHint') }}
          </p>

          <template v-if="shiftImport.missingCaregivers.length">
            <div class="font-weight-medium mt-2">
              {{ t('shiftImport.missingCaregivers') }} ({{ shiftImport.missingCaregivers.length }})
            </div>
            <div class="d-flex flex-wrap ga-1 mt-1">
              <v-chip
                v-for="dni in shiftImport.missingCaregivers"
                :key="`c-${dni}`"
                size="small"
                variant="outlined"
              >
                {{ dni }}
              </v-chip>
            </div>
          </template>

          <template v-if="shiftImport.missingPatients.length">
            <div class="font-weight-medium mt-3">
              {{ t('shiftImport.missingPatients') }} ({{ shiftImport.missingPatients.length }})
            </div>
            <div class="d-flex flex-wrap ga-1 mt-1">
              <v-chip
                v-for="dni in shiftImport.missingPatients"
                :key="`p-${dni}`"
                size="small"
                variant="outlined"
              >
                {{ dni }}
              </v-chip>
            </div>
          </template>

          <template v-if="shiftImport.rowErrors.length">
            <div class="font-weight-medium mt-3">
              {{ t('shiftImport.rowErrors') }} ({{ shiftImport.rowErrors.length }})
            </div>
            <v-list density="compact">
              <v-list-item
                v-for="(error, index) in shiftImport.rowErrors"
                :key="`e-${index}`"
                :title="rowErrorTitle(error)"
                :subtitle="rowErrorDnis(error)"
              />
            </v-list>
          </template>
        </template>

        <v-row
          v-if="status === 'previewed' || canReupload"
          class="ma-0 mt-4"
        >
          <v-btn
            v-if="canReupload"
            variant="outlined"
            prepend-icon="mdi-arrow-left"
            :href="reuploadHref"
          >
            {{ t('shiftImport.reupload') }}
          </v-btn>
          <v-spacer />
          <v-btn
            v-if="canConfirm"
            color="primary"
            :loading="isCommitting || awaitingCommit"
            @click="showConfirmDialog = true"
          >
            {{ t('shiftImport.confirm') }}
          </v-btn>
        </v-row>

        <v-alert
          v-if="commitFailed"
          type="error"
          class="mt-4"
        >
          {{ t('shiftImport.genericError') }}
        </v-alert>
      </v-card>

      <responsive-dialog
        v-if="canConfirm"
        v-model="showConfirmDialog"
        max-width="480"
        :mobile-fullscreen="false"
      >
        <v-card>
          <v-card-title class="text-wrap">
            {{ t('shiftImport.confirmDialogTitle') }}
          </v-card-title>
          <v-card-text>
            {{ t('shiftImport.confirmDialogText', {
              count: shiftImport.preview.wouldCreate ?? 0,
              period: periodLabel,
            }) }}
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              variant="text"
              @click="showConfirmDialog = false"
            >
              {{ t('shiftImport.cancel') }}
            </v-btn>
            <v-btn
              color="primary"
              @click="confirmCommit"
            >
              {{ t('shiftImport.confirm') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </responsive-dialog>
    </template>
  </v-container>
</template>

<style scoped>
.error-alert-preline {
  white-space: pre-line;
}
</style>
